import { useState } from "react";
import { cn } from "@/lib/utils";
import { colorStyles, fmtHours, HU_DAY_LABELS } from "@/pages/schedule/helpers";

// ---------------------------------------------------------------------------
// MonthGrid — naptár-stílusú havi nézet
// - 7 oszlop (H–V), 5-6 sor a hónap napjaira
// - Cellában: dátum (jobb felső) + adott napi shiftek pici színes blokkokként
// - Hónapon kívüli napok halványabbak
// - "Ma": indigo kör
// - Cellára kattintva (vagy shift blokkra) lehet bulk-select-elni vagy szerkeszteni
// ---------------------------------------------------------------------------
export default function MonthGrid({
  section,
  monthStart,   // YYYY-MM-01 (a hónap első napja)
  monthEnd,     // YYYY-MM-{n} utolsó napja
  selectedIds,
  onToggleSelect,
  onShiftClick,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
}) {
  const employees = section.employees || [];

  // Az összes shift a hónapra (employees-en keresztül)
  const allShifts = [];
  employees.forEach((e) =>
    (e.shifts || []).forEach((sh) =>
      allShifts.push({ ...sh, employee_name: e.name, employee_id: e._id })),
  );
  const shiftsByDate = {};
  allShifts.forEach((sh) => {
    shiftsByDate[sh.date] = shiftsByDate[sh.date] || [];
    shiftsByDate[sh.date].push(sh);
  });

  // Összes tervezet a hónapra
  const allDrafts = [];
  employees.forEach((e) =>
    (e.drafts || []).forEach((dr) =>
      allDrafts.push({ ...dr, employee_name: e.name, employee_id: e._id })),
  );
  const draftsByDate = {};
  allDrafts.forEach((dr) => {
    draftsByDate[dr.date] = draftsByDate[dr.date] || [];
    draftsByDate[dr.date].push(dr);
  });

  // Naptár rács generálása
  const monthDate = new Date(monthStart + "T00:00:00");
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // Mon=0
  const lastDay = new Date(year, month + 1, 0).getDate();
  // Calendar grid összesen 6 sor × 7 nap = 42 cell
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const dayNum = i - firstDow + 1;
    if (dayNum < 1) {
      const prev = new Date(year, month, dayNum);
      cells.push({ date: toIso(prev), outside: true });
    } else if (dayNum > lastDay) {
      const next = new Date(year, month, dayNum);
      cells.push({ date: toIso(next), outside: true });
    } else {
      const inside = new Date(year, month, dayNum);
      cells.push({ date: toIso(inside), outside: false });
    }
  }
  // Ha az utolsó sor üres, dropoljuk azt is (esthet 5 sor)
  const rowCount = cells[35].outside && cells[41].outside ? 5 : 6;
  const visibleCells = cells.slice(0, rowCount * 7);

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_-18px_rgba(15,23,42,0.16)] dark:border-border dark:bg-card">
      {/* HEADER: H K Sze Cs P Szo V */}
      <div className="grid grid-cols-7 border-b border-slate-200/80 bg-slate-50/60 dark:border-border dark:bg-foreground/[0.02]">
        {HU_DAY_LABELS.map((d, i) => (
          <div
            key={d.long}
            className={cn(
              "py-2.5 text-center text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-muted-foreground",
              i >= 5 && "text-slate-400",
            )}
          >
            {d.long}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7" style={{ gridAutoRows: "minmax(120px, auto)" }}>
        {visibleCells.map((cell, idx) => {
          const items = (shiftsByDate[cell.date] || []).sort(
            (a, b) => (a.start_time || "").localeCompare(b.start_time || ""),
          );
          const isToday = cell.date === todayIso && !cell.outside;
          const dow = idx % 7;
          const isWeekend = dow >= 5;
          const dayNum = Number(cell.date.slice(-2));
          return (
            <div
              key={idx}
              className={cn(
                "relative flex flex-col gap-1 border-b border-r border-slate-100 p-1.5 dark:border-border/60",
                cell.outside && "bg-slate-50/40 dark:bg-foreground/[0.015]",
                isWeekend && !cell.outside && "bg-slate-50/60 dark:bg-foreground/[0.025]",
                isToday && "bg-indigo-50/30 dark:bg-indigo-500/[0.05]",
                idx % 7 === 6 && "border-r-0",
              )}
            >
              {/* Date badge */}
              <div className="flex items-center justify-end">
                {isToday ? (
                  <span
                    className="grid h-6 w-6 place-items-center rounded-full bg-indigo-600 font-mono text-[11px] font-semibold text-white shadow-[0_2px_8px_-2px_rgba(79,70,229,0.45)]"
                    aria-label="Ma"
                  >
                    {dayNum}
                  </span>
                ) : (
                  <span
                    className={cn(
                      "font-mono text-[12px] font-medium tabular-nums",
                      cell.outside
                        ? "text-slate-300 dark:text-muted-foreground/40"
                        : "text-slate-600 dark:text-foreground/80",
                    )}
                  >
                    {dayNum}
                  </span>
                )}
              </div>

              {/* Shifts */}
              <div className="flex flex-col gap-0.5 overflow-hidden">
                {items.slice(0, 4).map((sh) => {
                  const cs = colorStyles(sh.color);
                  const selected = selectedIds?.has(sh._id);
                  return (
                    <button
                      key={sh._id}
                      type="button"
                      onClick={(e) => {
                        if (e.shiftKey || e.metaKey || e.ctrlKey) {
                          onToggleSelect?.(sh._id);
                          return;
                        }
                        onShiftClick?.(sh);
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        onToggleSelect?.(sh._id);
                      }}
                      className={cn(
                        "relative flex w-full items-center gap-1 overflow-hidden rounded-[5px] border-[0.5px] px-1 py-[1px] text-left font-mono text-[9.5px] font-semibold tabular-nums leading-tight",
                        cs.block,
                        selected && "ring-2 ring-indigo-500/60 ring-offset-1 ring-offset-background",
                      )}
                      title={`${sh.employee_name} — ${sh.template_name} ${sh.start_time}–${sh.end_time}`}
                      data-testid={`month-shift-${sh._id}`}
                    >
                      <span className={cn("h-2 w-1 shrink-0 rounded-full", cs.dot)} aria-hidden />
                      <span className="truncate font-sans font-medium">
                        {sh.employee_name}
                      </span>
                    </button>
                  );
                })}
                {items.length > 4 && (
                  <span className="text-[9px] font-medium text-slate-500 dark:text-muted-foreground">
                    +{items.length - 4} további
                  </span>
                )}
                {/* Drafts — szaggatott pill, mini Jóváhagy gombbal */}
                {(draftsByDate[cell.date] || []).slice(0, 3).map((dr) => {
                  const cs = colorStyles(dr.color || (dr.is_day_off ? "slate" : "indigo"));
                  const id = dr.id || dr._id;
                  const loading = approvingDraftId === id;
                  return (
                    <div
                      key={id}
                      className={cn(
                        "flex w-full items-center gap-1 overflow-hidden rounded-[5px] border border-dashed bg-white/40 px-1 py-[1px] text-[9.5px] leading-tight dark:bg-card/30",
                        cs.block.replace(/bg-\S+/g, ""),
                      )}
                      title={`Tervezet: ${dr.employee_name} — ${dr.template_name || (dr.is_day_off ? "Szabadnap" : "")}`}
                      data-testid={`month-draft-${id}`}
                    >
                      <span className={cn("h-2 w-1 shrink-0 rounded-full", cs.dot)} aria-hidden />
                      <span className="truncate flex-1 font-sans font-medium">
                        {dr.employee_name}
                      </span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!loading) onApproveDraft?.(dr);
                        }}
                        disabled={loading}
                        aria-label="Jóváhagy"
                        data-testid={`month-draft-approve-${id}`}
                        className="grid h-3.5 w-3.5 shrink-0 place-items-center rounded-full bg-emerald-500 text-white shadow-[0_2px_4px_-1px_rgba(16,185,129,0.6)] transition hover:bg-emerald-600 disabled:opacity-50"
                      >
                        <span className="text-[8px] font-bold">✓</span>
                      </button>
                    </div>
                  );
                })}
                {(draftsByDate[cell.date] || []).length > 3 && (
                  <span className="text-[9px] font-medium text-amber-700 dark:text-amber-400">
                    +{(draftsByDate[cell.date] || []).length - 3} tervezet
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function toIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
